import { useCallback } from 'react';
import produce from 'immer';
import _filter from 'lodash/filter';
import _first from 'lodash/first';
import _isEmpty from 'lodash/isEmpty';
import BASKET_MODES from '@constants/basketModes';
import BASKET_ROWS_TYPES from '@constants/basketRowsTypes';
import DIET_LENGTH_MODE from '@constants/dietLengtMode';
import useOrderForm from '@hooks/useOrderForm';
import { format } from '@services/Date.service';

const useTransformResponseBasketRedux = () => {
  const {
    data: {
      customDietLengthEnabled,
      packages,
      packageLineFilters,
      predefinedDietLengthEnabled,
      diets,
    },
  } = useOrderForm();

  const dietLengthMode =
    predefinedDietLengthEnabled && !customDietLengthEnabled
      ? DIET_LENGTH_MODE.PREDEFINED
      : DIET_LENGTH_MODE.CUSTOM;

  const transformResponseDietAddon = useCallback(
    ({ addon, quantity, type }) => ({
      addon: addon['@id'],
      type,
      quantity,
    }),
    []
  );

  const transformResponseDietRow = useCallback(
    dietRow => {
      const {
        '@id': iri,
        '@type': typeIri,
        days = [],
        options = {},
        ...diet
      } = dietRow;

      const isEmptyAddress = _isEmpty(diet?.address);
      const isEmptyPickUpPoint = _isEmpty(diet?.pickUpPoint);

      const activePackageIndex = packages.findIndex(
        pack => pack['@id'] === diet?.package
      );

      const activePackage =
        // eslint-disable-next-line no-negated-condition -- FIXME
        activePackageIndex !== -1
          ? diet?.package
          : packages.find(({ activeDietsIds }) =>
              activeDietsIds.includes(diet?.diet?.id)
            )?.['@id'];

      const packageLineFilter = packageLineFilters?.find(filter =>
        filter.activePackagesIds.includes(
          // eslint-disable-next-line radix -- FIXME
          parseInt(activePackage?.split('/').pop())
        )
      );
      return {
        '@id': iri,
        '@type': typeIri,
        id: diet?.diet.id,
        addons: diet?.addons?.map(transformResponseDietAddon) ?? [],
        // eslint-disable-next-line no-negated-condition -- FIXME
        address: !isEmptyAddress ? diet.address : {},
        calorificId: diet?.calorific?.['@id'] ?? null,
        connectedElements: {},
        deliveryDates: Array.isArray(days) ? days : [...Object.values(days)],
        // eslint-disable-next-line no-negated-condition, no-nested-ternary -- FIXME
        deliveryType: !isEmptyAddress
          ? 'ADDRESS'
          : // eslint-disable-next-line no-negated-condition -- FIXME
          !isEmptyPickUpPoint
          ? 'PICKUP_POINT'
          : null,
        mealTypes: diet?.mealTypes
          ? (diet?.mealTypes ?? []).map(mealType => mealType?.['@id'])
          : undefined,
        dietId: diet?.diet?.['@id'] ?? null,
        dietLength: diet?.dietLength,
        dietLengthMode: diet?.dietLengthMode ?? dietLengthMode,
        firstDeliveryDay: days?.[0],
        optionChangeMenu: options?.optionChangeMenu,
        packageId: activePackage ?? null,
        packageLineFilters: packageLineFilter ?? null,
        paymentMode: diet?.paymentMode,
        // eslint-disable-next-line no-negated-condition -- FIXME
        pickUpPoint: !isEmptyPickUpPoint ? diet.pickUpPoint : {},
        saturdayInclude: options?.saturdayInclude,
        sundayInclude: options?.sundayInclude,
        useEcoContainers: options?.useEcoContainers,
        variantId: diet?.variant?.['@id'] ?? null,
        allowStepWithAdditionalMealTypes: diets.find(
          ({ id }) => id === diet?.diet.id
        ).allowStepWithAdditionalMealTypes,
        hasDietsToSelectMenu: diets.find(({ id }) => id === diet?.diet.id)
          .hasDietsToSelectMenu,
        price: diet?.price,
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
    [packages]
  );

  const transformResponseBasketRedux = useCallback(
    (response, basketItems = []) => {
      const currentReduxDietElements = basketItems?.dietElements;
      let dietElements = _filter(response.rows, [
        '@type',
        BASKET_ROWS_TYPES.DIET_ITEM,
      ]);
      // Attemp to keep dietLengthMode from redux store
      if (
        !_isEmpty(currentReduxDietElements) &&
        currentReduxDietElements?.length === dietElements?.length
      ) {
        dietElements = dietElements.map((dietElement, index) => ({
          ...dietElement,
          dietLengthMode: currentReduxDietElements?.[index]?.dietLengthMode,
        }));
      }

      const shopElements = _filter(response.rows, item =>
        [BASKET_ROWS_TYPES.ADDON_ITEM, BASKET_ROWS_TYPES.DISH_ITEM].includes(
          item['@type']
        )
      );

      const connectedElementsByDietsIri = shopElements.reduce(
        (acc, item) =>
          produce(acc, draftState => {
            const { connectedTo, day, clientDiet } = item;
            const connectedToIri = connectedTo?.['@id'] ?? 'notConnected';
            const clientDietIri = clientDiet ?? 'noDiet';
            const formattedDay = format(new Date(day));

            draftState[connectedToIri] ??= {};
            draftState[connectedToIri][clientDietIri] ??= {};
            draftState[connectedToIri][clientDietIri][formattedDay] ??= {};

            let key;
            if (
              item['@type'] === BASKET_ROWS_TYPES.DISH_ITEM &&
              item.dishSize
            ) {
              key = item.dishSize['@id'];
            }
            if (item['@type'] === BASKET_ROWS_TYPES.ADDON_ITEM && item.addon) {
              key = item.addon['@id'];
            }
            if (key) {
              draftState[connectedToIri][clientDietIri][formattedDay][key] =
                item.quantity;
            }

            draftState[connectedToIri][clientDietIri][formattedDay][key] =
              item.quantity;
          }),
        {}
      );

      const convertedDietElements = dietElements.map(transformResponseDietRow);

      const items = Object.entries(connectedElementsByDietsIri).reduce(
        (acc, [connectedToIri, clientDietItems]) =>
          produce(acc, draftState => {
            const connectedDietIndex = draftState.dietElements.findIndex(
              diet => diet['@id'] === connectedToIri
            );

            // eslint-disable-next-line no-negated-condition -- FIXME
            if (connectedDietIndex !== -1) {
              draftState.dietElements[connectedDietIndex].connectedElements =
                clientDietItems || {};
            } else {
              Object.entries(clientDietItems || {}).forEach(
                ([clientDietIri, daysMap]) => {
                  draftState.others[clientDietIri] ??= {};

                  Object.entries(daysMap || {}).forEach(([day, itemsMap]) => {
                    draftState.others[clientDietIri][day] = {
                      ...(draftState.others[clientDietIri][day] ?? {}),
                      ...(itemsMap ?? {}),
                    };
                  });
                }
              );
            }
          }),
        { dietElements: convertedDietElements, others: {} }
      );

      const noDietCollapsedDays = shopElements.reduce((acc, item) => {
        const formattedDate = format(new Date(item.day));
        return {
          ...acc,
          ...(item.connectedTo?.['@id']
            ? { [formattedDate]: { isOpen: false } }
            : {}),
        };
      }, {});

      return {
        activeOrderDietIri: _first(dietElements)?.['@id'] ?? null,
        basketId: response?.id ?? null,
        items: {
          dietElements: items.dietElements,
          ...items.others,
        },
        collapsed: {
          noDiet: { isOpen: true, days: noDietCollapsedDays },
        },
        useDiscountCode: !_isEmpty(response?.discountCode?.['@id']),
        discountCode: {
          '@id': response?.discountCode?.['@id'] ?? null,
          value: response?.discountCode?.code ?? '',
        },
        paymentMethod: response?.paymentType ?? null,
        isRedirectingToConfirm: false,
        basketMode: BASKET_MODES.NORMAL,
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
    [packages, transformResponseDietRow]
  );

  return [transformResponseBasketRedux];
};

export default useTransformResponseBasketRedux;
